import ColumnIcon from '../../../assets/icons/Column.svg?react';
import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushRect, pushSegment } from '../glyphs/frame';
import type { ParamValues } from '../paramSchema';
import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import { t } from '../../translation/markers';

/** Segments approximating the round column's ellipse. */
const ROUND_SEGMENTS = 24;

const PARAMS = {
  shape: {
    kind: 'select',
    label: t('shape'),
    default: 'rectangular',
    options: [
      { value: 'rectangular', label: t('rectangular') },
      { value: 'round', label: t('round') },
    ],
  },
  // Vertical bound, mirroring the floorplan STRUCTURE's `structure_height`.
  // The glyph is 2D so it doesn't affect the drawing — 3.0 matches the
  // structure tools' orphan-column default.
  height: { kind: 'float', label: t('height'), default: 3.0, min: 0, decimals: 2, distance: true },
} as const;

/**
 * Column (layout equivalent of a COLUMN structure): a point-placed footprint
 * drawn with the classic plan symbol — the outline crossed by both diagonals.
 * The `shape` param picks the outline: rectangular (width × depth box) or
 * round (an ellipse inscribed in the footprint). The `height` bound matches
 * the source structure's extrusion height and is kept by the floor→layout
 * conversion.
 */
function buildColumnGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;

  if (params.shape === 'round') {
    // Ellipse inscribed in the footprint.
    let prev: Pt2 | null = null;
    for (let i = 0; i <= ROUND_SEGMENTS; i++) {
      const t = (2 * Math.PI * i) / ROUND_SEGMENTS;
      const pt = at(frame, hw * Math.cos(t), hd * Math.sin(t));
      if (prev) pushSegment(out, prev, pt, frame.z);
      prev = pt;
    }
    // Diagonal cross clipped to the ellipse (the 45° points).
    const cu = hw * Math.SQRT1_2;
    const cn = hd * Math.SQRT1_2;
    pushSegment(out, at(frame, -cu, -cn), at(frame, cu, cn), frame.z);
    pushSegment(out, at(frame, -cu, cn), at(frame, cu, -cn), frame.z);
  } else {
    pushRect(out, frame, -hw, hw, -hd, hd);
    pushSegment(out, at(frame, -hw, -hd), at(frame, hw, hd), frame.z);
    pushSegment(out, at(frame, -hw, hd), at(frame, hw, -hd), frame.z);
  }

  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'column',
    label: t('Column'),
    category: SketchObjectCategory.STRUCTURE,
    anchor: SketchObjectAnchorKind.POINT,
    constraint: { kind: 'FREE' },
    defaultSize: { width: 0.3, depth: 0.3 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildColumnGlyph },
    // Same icon the STRUCTURE (floorplan) columns use in the panel.
    icon: ColumnIcon,
  }),
);
